import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';

import { cn } from '@/styles/tailwind-utils';
import { AccountLayout } from '@components/layout/AccountLayout';
import { ClientProfileDrawer, useClient, useRecordConsent, useUpdateClientProfile } from '@features/clients';
import type {
  ClientAddress,
  ClientListItem,
  ClientProfile,
  UpdateClientProfileRequest,
} from '@features/clients';

// ─── Tabs ─────────────────────────────────────────────────────────────────────

const CLIENT_VIEW_TABS = ['overview', 'medicalNotes', 'billing', 'documents', 'outcomes'] as const;
type ClientViewTab = (typeof CLIENT_VIEW_TABS)[number];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-violet-500',
  'bg-sky-500',
  'bg-emerald-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-indigo-500',
  'bg-teal-500',
  'bg-orange-500',
  'bg-cyan-600',
  'bg-lime-600',
];

function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? 'bg-gray-500';
}

function computeAge(dateOfBirth: string): number {
  const dob = new Date(dateOfBirth + 'T12:00:00');
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

function formatDOB(dateOfBirth: string): string {
  const date = new Date(dateOfBirth + 'T12:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Build human-readable address lines from the Romanian address fields. */
function formatAddressLines(address: ClientAddress | null | undefined): string[] {
  if (!address) return [];

  const streetLine = [address.street, address.streetNumber].filter(Boolean).join(' ');
  const unitParts: string[] = [];
  if (address.building) unitParts.push(`bl. ${address.building}`);
  if (address.entrance) unitParts.push(`sc. ${address.entrance}`);
  if (address.apartment) unitParts.push(`ap. ${address.apartment}`);
  const line1 = [streetLine, ...unitParts].filter(Boolean).join(', ');

  const localityParts = [address.city, address.sector, address.county].filter(Boolean).join(', ');
  const line2 = [address.postalCode, localityParts].filter(Boolean).join(' ');

  return [line1, line2].map((l) => l.trim()).filter((l) => l.length > 0);
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 10L6 7l3-3" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8 19.79 19.79 0 01.01 2.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

function WarningIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

/**
 * Build a full-replacement PUT body from the current profile, optionally
 * overriding individual fields. Used by the sidebar's inline edits, which only
 * touch one field but must echo the whole updatable set (API spec §5.4).
 */
function profileToUpdateRequest(
  p: ClientProfile,
  overrides: Partial<UpdateClientProfileRequest> = {},
): UpdateClientProfileRequest {
  return {
    version: p.version ?? 0,
    firstName: p.firstName,
    lastName: p.lastName,
    preferredName: p.preferredName ?? null,
    dateOfBirth: p.dateOfBirth,
    sex: p.sex ?? null,
    phone: p.phone,
    email: p.email,
    address: {
      county: p.address?.county ?? null,
      city: p.address?.city ?? null,
      sector: p.address?.sector ?? null,
      street: p.address?.street ?? null,
      streetNumber: p.address?.streetNumber ?? null,
      building: p.address?.building ?? null,
      entrance: p.address?.entrance ?? null,
      apartment: p.address?.apartment ?? null,
      postalCode: p.address?.postalCode ?? null,
      country: p.address?.country ?? 'RO',
    },
    preferredContactMethod: p.preferredContactMethod ?? null,
    emergencyContact: {
      fullName: p.emergencyContact?.fullName ?? null,
      relationship: p.emergencyContact?.relationship ?? null,
      phone: p.emergencyContact?.phone ?? null,
      email: p.emergencyContact?.email ?? null,
    },
    referralSource: p.referralSource ?? null,
    referredBy: p.referredBy ?? null,
    occupation: p.occupation ?? null,
    employer: p.employer ?? null,
    internalNotes: p.internalNotes ?? null,
    medicalAlert: p.medicalAlert ?? null,
    ...overrides,
  };
}

/**
 * Inline editor for the medical alert. Mounted only while editing, so its draft
 * seeds cleanly from the current value via the useState initializer.
 */
function MedicalAlertEditor({
  initialValue,
  isSaving,
  onCancel,
  onSave,
}: {
  initialValue: string;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialValue);

  const trimmed = draft.trim();
  const canSave = !isSaving && trimmed !== '' && trimmed !== initialValue;

  return (
    <div className="mt-1.5">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={3}
        disabled={isSaving}
        autoFocus
        className="w-full rounded-sm border border-amber-300 bg-white px-2.5 py-1.5 text-sm leading-snug text-amber-900 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-60"
      />
      <div className="mt-2 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="rounded-sm px-2.5 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100 disabled:opacity-50"
        >
          {t('clients.view.sidebar.cancel')}
        </button>
        <button
          type="button"
          onClick={() => onSave(trimmed)}
          disabled={!canSave}
          className="rounded-sm bg-amber-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
        >
          {isSaving ? t('clients.view.sidebar.saving') : t('clients.view.sidebar.save')}
        </button>
      </div>
    </div>
  );
}

/**
 * Amber warning card surfacing a safety-critical medical alert. Rendered only
 * when set; supports inline editing via the pencil icon.
 */
function MedicalAlertCard({
  alert,
  isEditing,
  isSaving,
  onEdit,
  onCancel,
  onSave,
}: {
  alert: string;
  isEditing: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="flex gap-2.5 rounded-md border border-amber-200/80 bg-amber-50 px-4 py-3 text-amber-800">
      <span className="mt-0.5 shrink-0 text-amber-500" aria-hidden="true">
        <WarningIcon />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80">
            {t('clients.view.sidebar.medicalAlertLabel')}
          </p>
          {!isEditing && (
            <button
              type="button"
              onClick={onEdit}
              aria-label={t('clients.view.sidebar.editMedicalAlert')}
              title={t('clients.view.sidebar.editMedicalAlert')}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-amber-600 transition-colors hover:bg-amber-100 hover:text-amber-800 focus:outline-none focus:ring-2 focus:ring-amber-400"
            >
              <EditIcon />
            </button>
          )}
        </div>

        {isEditing ? (
          <MedicalAlertEditor
            initialValue={alert}
            isSaving={isSaving}
            onCancel={onCancel}
            onSave={onSave}
          />
        ) : (
          <p className="mt-0.5 whitespace-pre-line text-sm leading-snug">{alert}</p>
        )}
      </div>
    </div>
  );
}

/** Subtle prompt shown when no medical alert is set yet; opens the inline editor. */
function AddMedicalAlertButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-amber-300 bg-amber-50/40 px-4 py-2.5 text-sm font-medium text-amber-700 transition-colors hover:border-amber-400 hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-400"
    >
      <PlusIcon />
      {t('clients.view.sidebar.addMedicalAlert')}
    </button>
  );
}

/** Blue info card surfacing staff-only internal notes. Rendered only when set. */
function InternalNotesCard({ notes }: { notes: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2.5 rounded-md border border-blue-200/80 bg-blue-50 px-4 py-3 text-blue-800">
      <span className="mt-0.5 shrink-0 text-blue-500" aria-hidden="true">
        <InfoIcon />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-blue-700/80">
          {t('clients.view.sidebar.internalNoteLabel')}
        </p>
        <p className="mt-0.5 whitespace-pre-line text-sm leading-snug">{notes}</p>
      </div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  children: React.ReactNode;
}

function InfoRow({ label, children }: InfoRowProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="shrink-0 text-sm text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right text-sm font-medium text-foreground">{children}</dd>
    </div>
  );
}

function maskCnp(cnp: string): string {
  if (cnp.length <= 4) return cnp;
  return '*'.repeat(cnp.length - 4) + cnp.slice(-4);
}

function ContactDemographicsCard({ profile, onEdit }: { profile: ClientProfile; onEdit: () => void }) {
  const { t } = useTranslation();
  const addressLines = formatAddressLines(profile.address);
  const emergency = profile.emergencyContact;
  const cnp = profile.identifiers?.cnp;

  return (
    <div className="rounded-md border border-border bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('clients.view.sidebar.contactTitle')}
        </h2>
        <button
          type="button"
          onClick={onEdit}
          aria-label={t('clients.view.sidebar.edit')}
          title={t('clients.view.sidebar.edit')}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
        >
          <EditIcon />
        </button>
      </div>

      <dl className="divide-y divide-border/60 px-4 py-1">
        {cnp && (
          <InfoRow label={t('clients.view.sidebar.cnp')}>
            <span className="font-mono tracking-wider">{maskCnp(cnp)}</span>
          </InfoRow>
        )}

        {profile.phone && (
          <InfoRow label={t('clients.view.sidebar.phone')}>
            <a href={`tel:${profile.phone}`} className="hover:underline">
              {profile.phone}
            </a>
          </InfoRow>
        )}

        {profile.email && (
          <InfoRow label={t('clients.view.sidebar.email')}>
            <a href={`mailto:${profile.email}`} className="break-all text-brand hover:underline">
              {profile.email}
            </a>
          </InfoRow>
        )}

        {addressLines.length > 0 && (
          <InfoRow label={t('clients.view.sidebar.address')}>
            <span className="whitespace-pre-line">{addressLines.join('\n')}</span>
          </InfoRow>
        )}

        {profile.occupation && (
          <InfoRow label={t('clients.view.sidebar.occupation')}>{profile.occupation}</InfoRow>
        )}

        {profile.referredBy && (
          <InfoRow label={t('clients.view.sidebar.referral')}>{profile.referredBy}</InfoRow>
        )}

        {emergency?.fullName && (
          <InfoRow label={t('clients.view.sidebar.emergency')}>
            <span className="flex flex-col text-brand">
              <span>{emergency.fullName}</span>
              {emergency.phone && (
                <a href={`tel:${emergency.phone}`} className="hover:underline">
                  {emergency.phone}
                </a>
              )}
            </span>
          </InfoRow>
        )}
      </dl>
    </div>
  );
}

function ContactDemographicsSkeleton() {
  return (
    <div className="rounded-md border border-border bg-white p-4 shadow-sm">
      <div className="mb-4 h-3 w-40 animate-pulse rounded bg-muted" />
      <div className="space-y-3">
        {Array.from({ length: 5 }, (_, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="h-3 w-16 animate-pulse rounded bg-muted" />
            <div className="h-3 w-28 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AppointmentNotificationsCard({
  profile,
  clientId,
}: {
  profile: ClientProfile;
  clientId: string;
}) {
  const { t } = useTranslation();
  const { mutate: recordConsent, isPending } = useRecordConsent();

  const hasEmail = !!profile.email;
  const hasPhone = !!profile.phone;

  // Local state is the source of truth for display. Initialised once from the
  // server value when the component mounts (profile is always defined here).
  // It updates optimistically on toggle and reverts on API error — this avoids
  // the checked↔unchecked flicker that occurs when the value is derived directly
  // from server state and the query invalidation / refetch cycle runs.
  const [emailChecked, setEmailChecked] = useState(
    profile.communicationConsent?.appointmentReminders?.email ?? false,
  );
  const [smsChecked, setSmsChecked] = useState(
    profile.communicationConsent?.appointmentReminders?.sms ?? false,
  );

  function handleToggle(channel: 'email' | 'sms', currentValue: boolean) {
    const newValue = !currentValue;

    if (channel === 'email') setEmailChecked(newValue);
    else setSmsChecked(newValue);

    recordConsent(
      {
        clientId,
        request: {
          consentType:
            channel === 'email' ? 'APPOINTMENT_REMINDERS_EMAIL' : 'APPOINTMENT_REMINDERS_SMS',
          consentVersion: '1.0.0',
          granted: newValue,
          collectedBy: 'STAFF',
        },
      },
      {
        onError: () => {
          if (channel === 'email') setEmailChecked(currentValue);
          else setSmsChecked(currentValue);
        },
      },
    );
  }

  const emailDisabled = !hasEmail;
  const smsDisabled = !hasPhone;

  return (
    <div className="rounded-md border border-border bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('clients.view.sidebar.appointmentNotifications.title')}
        </h2>
        {isPending && (
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground/70">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-muted-foreground/50" />
            {t('clients.view.sidebar.saving')}
          </span>
        )}
      </div>
      <div className="divide-y divide-border/60 px-4 py-1">
        {/* Email row */}
        <div className="py-2.5">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              role="switch"
              aria-checked={emailChecked}
              disabled={emailDisabled || isPending}
              onClick={() => handleToggle('email', emailChecked)}
              className={cn(
                'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent',
                'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                emailChecked ? 'bg-brand' : 'bg-border',
                emailDisabled || isPending ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200',
                  emailChecked ? 'translate-x-4' : 'translate-x-0',
                )}
              />
            </button>
            <span className={cn('text-sm', emailDisabled && 'text-muted-foreground')}>
              {t('clients.view.sidebar.appointmentNotifications.emailLabel')}
              {profile.email && (
                <span className="ml-1 font-normal text-muted-foreground">({profile.email})</span>
              )}
            </span>
          </div>
          {emailDisabled && (
            <p className="mt-1 pl-[46px] text-xs leading-tight text-muted-foreground">
              {t('clients.view.sidebar.appointmentNotifications.noEmailWarning')}
            </p>
          )}
        </div>
        {/* SMS row */}
        <div className="py-2.5">
          <div className="flex items-center gap-2.5">
            <button
              type="button"
              role="switch"
              aria-checked={smsChecked}
              disabled={smsDisabled || isPending}
              onClick={() => handleToggle('sms', smsChecked)}
              className={cn(
                'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full border-2 border-transparent',
                'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                smsChecked ? 'bg-brand' : 'bg-border',
                smsDisabled || isPending ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform duration-200',
                  smsChecked ? 'translate-x-4' : 'translate-x-0',
                )}
              />
            </button>
            <span className={cn('text-sm', smsDisabled && 'text-muted-foreground')}>
              {t('clients.view.sidebar.appointmentNotifications.smsLabel')}
              {profile.phone && (
                <span className="ml-1 font-normal text-muted-foreground">({profile.phone})</span>
              )}
            </span>
          </div>
          {smsDisabled && (
            <p className="mt-1 pl-[46px] text-xs leading-tight text-muted-foreground">
              {t('clients.view.sidebar.appointmentNotifications.noPhoneWarning')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ClientSidebar({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const { data: profile, isLoading, error } = useClient(clientId);
  const { mutate: updateProfile, isPending, error: updateError, reset } = useUpdateClientProfile();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isEditingAlert, setIsEditingAlert] = useState(false);

  const drawerErrorMessage = useMemo(() => {
    if (!updateError) return null;
    switch (updateError.code) {
      case 'EMAIL_ALREADY_EXISTS':
        return t('clients.errors.emailAlreadyExists');
      case 'CNP_ALREADY_EXISTS':
        return t('clients.errors.cnpAlreadyExists');
      default:
        return t('clients.errors.updateFailed');
    }
  }, [updateError, t]);

  function handleOpenDrawer() {
    reset();
    setIsDrawerOpen(true);
  }

  function handleSubmit(data: UpdateClientProfileRequest) {
    updateProfile({ clientId, request: data }, { onSuccess: () => setIsDrawerOpen(false) });
  }

  function handleSaveMedicalAlert(value: string) {
    if (!profile) return;
    updateProfile(
      { clientId, request: profileToUpdateRequest(profile, { medicalAlert: value }) },
      { onSuccess: () => setIsEditingAlert(false) },
    );
  }

  return (
    <aside className="space-y-4">
      {profile &&
        (profile.medicalAlert || isEditingAlert ? (
          <MedicalAlertCard
            alert={profile.medicalAlert ?? ''}
            isEditing={isEditingAlert}
            isSaving={isPending}
            onEdit={() => setIsEditingAlert(true)}
            onCancel={() => setIsEditingAlert(false)}
            onSave={handleSaveMedicalAlert}
          />
        ) : (
          <AddMedicalAlertButton onClick={() => setIsEditingAlert(true)} />
        ))}

      {profile?.internalNotes && <InternalNotesCard notes={profile.internalNotes} />}

      {isLoading ? (
        <ContactDemographicsSkeleton />
      ) : error ? (
        <div className="rounded-md border border-border bg-white px-4 py-6 text-center text-sm text-muted-foreground shadow-sm">
          {t('clients.view.sidebar.loadError')}
        </div>
      ) : profile ? (
        <>
          <ContactDemographicsCard profile={profile} onEdit={handleOpenDrawer} />
          <AppointmentNotificationsCard profile={profile} clientId={clientId} />
        </>
      ) : null}

      {profile && (
        <ClientProfileDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          profile={profile}
          onSubmit={handleSubmit}
          isSubmitting={isPending}
          errorMessage={drawerErrorMessage}
        />
      )}
    </aside>
  );
}

// ─── Sub-header ───────────────────────────────────────────────────────────────

function ClientSubHeader({ client }: { client: ClientListItem }) {
  const { t } = useTranslation();
  const initials = `${client.firstName.charAt(0)}${client.lastName.charAt(0)}`.toUpperCase();
  const color = avatarColor(`${client.firstName}${client.lastName}`);
  const age = client.dateOfBirth ? computeAge(client.dateOfBirth) : null;
  const dob = client.dateOfBirth ? formatDOB(client.dateOfBirth) : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 md:px-6">
      {/* Back link */}
      <Link
        to="/account/clients"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeftIcon />
        {t('clients.view.backToClients')}
      </Link>

      {/* Profile row */}
      <div className="flex items-center gap-4">
        <div
          className={cn(
            'flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-base font-semibold text-white',
            color,
          )}
          aria-hidden="true"
        >
          {initials}
        </div>

        <div>
          <h1 className="font-display text-xl font-semibold text-foreground">
            {client.firstName} {client.lastName}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {age !== null && dob !== null && (
              <span className="flex items-center gap-1.5">
                <CalendarIcon />
                {t('clients.view.ageYears', { count: age })}
                <span aria-hidden="true">·</span>
                {t('clients.view.dobLabel', { date: dob })}
              </span>
            )}
            {client.phone && (
              <span className="flex items-center gap-1.5">
                <PhoneIcon />
                {client.phone}
              </span>
            )}
            {client.email && (
              <span className="flex items-center gap-1.5">
                <EmailIcon />
                {client.email}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: string;
  valueClassName?: string;
}

function StatCard({ label, value, valueClassName }: StatCardProps) {
  return (
    <div className="rounded-md border border-border bg-white px-5 py-4 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={cn('mt-2 text-2xl font-semibold text-foreground', valueClassName)}>{value}</p>
    </div>
  );
}

/** Section header + empty state — no appointment data is wired up yet. */
function AppointmentsSection({ title }: { title: string }) {
  const { t } = useTranslation();
  return (
    <section className="overflow-hidden rounded-md border border-border bg-white shadow-sm">
      <div className="border-b border-border px-5 py-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h2>
      </div>
      <p className="px-5 py-10 text-center text-sm text-muted-foreground">
        {t('clients.view.overviewTab.noAppointments')}
      </p>
    </section>
  );
}

function OverviewTab() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      {/* Section 1 — summary stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label={t('clients.view.overviewTab.totalVisits')} value="0" />
        <StatCard label={t('clients.view.overviewTab.nextAppointment')} value="—" />
        <StatCard label={t('clients.view.overviewTab.outstandingBalance')} value="0" />
      </div>

      {/* Section 2 — upcoming appointments */}
      <AppointmentsSection title={t('clients.view.overviewTab.upcomingAppointments')} />

      {/* Section 3 — recent appointments */}
      <AppointmentsSection title={t('clients.view.overviewTab.recentAppointments')} />
    </div>
  );
}

// ─── Coming-soon tab placeholders ───────────────────────────────────────────────

/**
 * Compact "coming soon" frame for the not-yet-built tabs. Mirrors the Account
 * layout's full-page placeholders (gradient surface, brand mark, badge, ghost
 * preview) but scaled to sit inside the narrower tab panel beside the sidebar.
 */
function TabPlaceholder({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-border shadow-sm"
      style={{
        background:
          'radial-gradient(ellipse 80% 50% at 70% -10%, hsl(160 51% 24% / 0.08) 0%, transparent 70%), radial-gradient(ellipse 60% 45% at -10% 110%, hsl(160 51% 24% / 0.05) 0%, transparent 65%), white',
      }}
    >
      {/* Decorative ring ornament — top right */}
      <div className="pointer-events-none absolute -right-20 -top-20 text-primary" aria-hidden="true">
        <svg width="320" height="320" viewBox="0 0 320 320" fill="none">
          <circle cx="160" cy="160" r="140" stroke="currentColor" strokeWidth="1" opacity="0.08" />
          <circle cx="160" cy="160" r="100" stroke="currentColor" strokeWidth="1" opacity="0.07" />
          <circle cx="160" cy="160" r="60"  stroke="currentColor" strokeWidth="1.5" opacity="0.06" />
        </svg>
      </div>

      <div className="relative flex flex-col items-center px-6 pb-10 pt-12 text-center">
        {/* Brand icon mark */}
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary shadow-md">
          {icon}
        </div>

        {/* Coming soon badge */}
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3.5 py-1.5">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
          <span className="text-[11px] font-semibold uppercase tracking-widest text-primary">
            {t('layout.account.comingSoon')}
          </span>
        </div>

        {/* Title */}
        <h2 className="mb-2.5 font-display text-3xl font-semibold text-foreground">{title}</h2>

        {/* Description */}
        <p className="mb-10 max-w-sm text-sm leading-relaxed text-muted-foreground">{description}</p>

        {/* Ghost preview of the future layout */}
        <div className="w-full opacity-40">{children}</div>
      </div>
    </div>
  );
}

// Per-tab brand icon marks (white stroke on the primary tile).

function MedicalNotesIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 2h6a1 1 0 011 1v1h1a2 2 0 012 2v13a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2h1V3a1 1 0 011-1z" />
      <path d="M12 10v5M9.5 12.5h5" />
    </svg>
  );
}

function BillingIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20M6 15h4" />
    </svg>
  );
}

function DocumentsIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h6" />
    </svg>
  );
}

function OutcomesIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 3 3 5-6" />
      <path d="M19 7v3h-3" />
    </svg>
  );
}

/** Medical notes — a clinical timeline of entries. */
function MedicalNotesGhost() {
  return (
    <div className="space-y-3 text-left">
      {[88, 70, 55].map((w, i) => (
        <div key={i} className="flex gap-3">
          <div className="flex flex-col items-center pt-1">
            <div className="h-2.5 w-2.5 rounded-full bg-primary/30" />
            {i < 2 && <div className="mt-1 w-px flex-1 bg-border" />}
          </div>
          <div className="flex-1 rounded-2xl border border-border bg-white/60 p-4">
            <div className="mb-2.5 flex items-center justify-between">
              <div className="h-2.5 w-28 rounded-full bg-muted/60" />
              <div className="h-2 w-14 rounded-full bg-muted/35" />
            </div>
            <div className="space-y-1.5">
              <div className="h-2 rounded-full bg-muted/45" style={{ width: `${w}%` }} />
              <div className="h-2 w-2/3 rounded-full bg-muted/35" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Billing — balance tiles plus a short ledger of invoices. */
function BillingGhost() {
  const rows = [
    { w: 'w-28', status: 'bg-green-200/60' },
    { w: 'w-24', status: 'bg-amber-200/60' },
    { w: 'w-32', status: 'bg-red-200/60' },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {['w-16', 'w-12', 'w-20'].map((w, i) => (
          <div key={i} className="rounded-2xl border border-border bg-white/60 p-4">
            <div className={`mb-3 h-2 rounded-full bg-muted/55 ${w}`} />
            <div className="h-6 w-16 rounded-lg bg-muted/45" />
          </div>
        ))}
      </div>
      <div className="overflow-hidden rounded-2xl border border-border bg-white/60">
        {rows.map((row, i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-border/50 px-5 py-3.5 last:border-b-0"
          >
            <div className="h-7 w-7 shrink-0 rounded-full bg-primary/20" />
            <div className={`h-2.5 rounded-full bg-muted/55 ${row.w}`} />
            <div className="ml-auto h-2.5 w-14 rounded-full bg-muted/45" />
            <div className={`h-5 w-14 rounded-full ${row.status}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Documents — a grid of uploaded-file cards. */
function DocumentsGhost() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
      {['w-20', 'w-16', 'w-24', 'w-14', 'w-20', 'w-16'].map((w, i) => (
        <div
          key={i}
          className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-white/60 p-5"
        >
          <div className="flex h-11 w-9 items-center justify-center rounded-md bg-primary/15">
            <div className="h-4 w-5 rounded-sm bg-primary/25" />
          </div>
          <div className="w-full space-y-1.5">
            <div className={`mx-auto h-2 rounded-full bg-muted/55 ${w}`} />
            <div className="mx-auto h-1.5 w-10 rounded-full bg-muted/35" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Outcomes — a progress trend chart plus result metrics. */
function OutcomesGhost() {
  const bars = [40, 55, 48, 70, 62, 80, 75, 88];
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-border bg-white/60 p-6">
        <div className="mb-1 h-2.5 w-28 rounded-full bg-muted/60" />
        <div className="mb-5 h-2 w-16 rounded-full bg-muted/40" />
        <div className="flex h-28 items-end gap-2">
          {bars.map((h, i) => (
            <div key={i} className="flex-1 rounded-t-sm bg-primary/25" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {['w-12', 'w-16', 'w-10'].map((w, i) => (
          <div key={i} className="rounded-2xl border border-border bg-white/60 p-4">
            <div className={`mb-3 h-2 rounded-full bg-muted/55 ${w}`} />
            <div className="mb-2 h-6 w-14 rounded-lg bg-muted/45" />
            <div className="h-1.5 w-full rounded-full bg-muted/25">
              <div className="h-1.5 rounded-full bg-primary/30" style={{ width: `${50 + i * 18}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Renders the brand placeholder for whichever non-overview tab is active. */
function PlaceholderTab({ tab }: { tab: Exclude<ClientViewTab, 'overview'> }) {
  const { t } = useTranslation();
  const config = {
    medicalNotes: { icon: <MedicalNotesIcon />, ghost: <MedicalNotesGhost /> },
    billing: { icon: <BillingIcon />, ghost: <BillingGhost /> },
    documents: { icon: <DocumentsIcon />, ghost: <DocumentsGhost /> },
    outcomes: { icon: <OutcomesIcon />, ghost: <OutcomesGhost /> },
  }[tab];

  return (
    <TabPlaceholder
      icon={config.icon}
      title={t(`clients.view.tabs.${tab}`)}
      description={t(`clients.view.placeholders.${tab}`)}
    >
      {config.ghost}
    </TabPlaceholder>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ClientViewPage() {
  const { t } = useTranslation();
  const { clientId } = useParams<{ clientId: string }>();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<ClientViewTab>('overview');
  const client = (location.state as { client?: ClientListItem } | null)?.client;

  if (!client || client.clientId !== clientId) {
    return <Navigate to="/account/clients" replace />;
  }

  return (
    <AccountLayout>
      {/* Header + tabs share a single white surface */}
      <div className="border-b border-border bg-white shadow-sm">
        <ClientSubHeader client={client} />

        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <nav
            role="tablist"
            aria-label={t('clients.view.tabsLabel')}
            className="-mb-px flex gap-1 overflow-x-auto"
          >
            {CLIENT_VIEW_TABS.map((tab) => {
              const isActive = activeTab === tab;
              return (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'whitespace-nowrap border-b-2 px-3.5 py-3 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t(`clients.view.tabs.${tab}`)}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content: persistent sidebar (left) + tab panel (right) */}
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
          {/* Sidebar — shown on every tab */}
          <div className="lg:order-1">
            <ClientSidebar clientId={client.clientId} />
          </div>

          {/* Tab panel — Overview is implemented; other tabs are empty for now */}
          <div
            role="tabpanel"
            aria-label={t(`clients.view.tabs.${activeTab}`)}
            className="min-h-[40vh] lg:order-2"
          >
            {activeTab === 'overview' ? <OverviewTab /> : <PlaceholderTab tab={activeTab} />}
          </div>
        </div>
      </div>
    </AccountLayout>
  );
}
